import React, { useEffect, useRef, useState, useCallback } from 'react';
import { siteData } from '../data/mock';

const DemosEvolvedSection = () => {
  const data = siteData.demosEvolved;
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 740px)');
    const onChange = () => setIsCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionHeight = sectionRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;

    // How far the section top has scrolled past the viewport top
    const scrolled = -rect.top;
    // Total scrollable distance within the section (section height minus one viewport)
    const scrollableDistance = sectionHeight - viewportHeight;

    if (scrollableDistance <= 0) return;

    const p = Math.max(0, Math.min(1, scrolled / scrollableDistance));
    setProgress(p);
  }, []);

  useEffect(() => {
    if (isCompact) {
      // On small screens, avoid sticky/scroll-animations that can hide content.
      setProgress(1);
      return;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isCompact]);

  // Map progress [0..1] → card visibility [0..4]
  // Cards appear faster with tighter intervals
  const getCardState = (index) => {
    const start = 0.05 + index * 0.12;
    const end = start + 0.12;
    const cardProgress = Math.max(0, Math.min(1, (progress - start) / (end - start)));
    // Apply ease-out cubic for butter-smooth feel
    return cardProgress < 1 ? 1 - Math.pow(1 - cardProgress, 3) : 1;
  };

  // Heading is always visible (no animation delay)
  const headingProgress = 1;

  return (
    <section
      ref={sectionRef}
      className="relative w-full"
      style={{
        /* Reduced height for faster scroll transition */
        height: isCompact ? 'auto' : '150vh',
      }}
    >
      {/* Sticky inner container */}
      <div
        style={{
          position: isCompact ? 'relative' : 'sticky',
          top: isCompact ? 'auto' : '77px', /* below fixed navbar */
          height: isCompact ? 'auto' : 'calc(100vh - 77px)',
          overflow: isCompact ? 'visible' : 'hidden',
          background: '#fff',
        }}
      >
        {/* Dot grid pattern background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'radial-gradient(circle, #c0c5ce 1px, transparent 1px)',
            backgroundSize: '16px 16px',
            opacity: 0.45,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* Content */}
        <div
          className="relative demos-layout"
          style={{
            padding: 'clamp(28px, 5.5vw, 40px) var(--page-gutter)',
            gap: '40px',
            alignItems: 'stretch',
            height: '100%',
            zIndex: 1,
          }}
        >
          {/* Left: heading + description */}
          <div
            style={{
              flex: '0 0 38%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              opacity: headingProgress,
              transform: `translateY(${(1 - headingProgress) * 20}px)`,
              willChange: 'transform, opacity',
            }}
          >
            <h2
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: '52px',
                fontWeight: 400,
                color: '#000',
                letterSpacing: '-0.025em',
                lineHeight: '54px',
                marginBottom: '20px',
              }}
            >
              {data.headingPrefix}{' '}
              <span style={{ color: '#FF98BF', fontStyle: 'italic' }}>
                {data.headingHighlight}
              </span>
            </h2>
            <p
              style={{
                fontFamily: 'InterVariable, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                fontStyle: 'italic',
                color: '#000',
                lineHeight: '22px',
                maxWidth: '380px',
              }}
            >
              {data.description}
            </p>
          </div>

          {/* Right: 2×2 card grid */}
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',
              gridTemplateRows: isCompact ? 'auto' : '1fr 1fr',
              gap: '16px',
              alignContent: isCompact ? 'start' : 'center',
            }}
          >
            {data.steps.map((step, i) => {
              const cp = isCompact ? 1 : getCardState(i);
              return (
                <div
                  key={i}
                  className="why-card-macos"
                  style={{
                    border: '1.5px solid #FF98BF',
                    borderRadius: '12px',
                    padding: 'clamp(18px, 4.8vw, 28px) clamp(16px, 4.4vw, 24px)',
                    background: '#fff',
                    cursor: 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: cp,
                    transform: `
                      scale(${0.92 + cp * 0.08})
                      translateY(${(1 - cp) * 14}px)
                    `,
                    filter: `blur(${(1 - cp) * 2}px)`,
                    willChange: 'transform, opacity, filter',
                  }}
                >
                  {/* Step label */}
                  <span
                    style={{
                      fontFamily: 'InterVariable, sans-serif',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#999',
                      display: 'block',
                      marginBottom: '16px',
                    }}
                  >
                    step {step.number}
                  </span>

                  {/* Step title */}
                  <StepTitle step={step} />

                  {/* Step description */}
                  <p
                    style={{
                      fontFamily: 'InterVariable, sans-serif',
                      fontSize: '15px',
                      fontWeight: 400,
                      fontStyle: 'italic',
                      color: '#000',
                      lineHeight: '20px',
                      marginTop: 'auto',
                    }}
                  >
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const StepTitle = ({ step }) => {
  if (step.titleSuffix) {
    return (
      <h3
        style={{
          fontFamily: 'Coolvetica, sans-serif',
          fontSize: 'clamp(24px, 6vw, 30px)',
          fontWeight: 400,
          letterSpacing: '-0.025em',
          lineHeight: 'clamp(28px, 6.4vw, 34px)',
          marginBottom: '16px',
          textAlign: 'center',
        }}
      >
        <span style={{ color: '#000' }}>{step.titleRest}</span>
        {step.number === '03' ? <br /> : ' '}
        <span style={{ color: '#FF98BF' }}>{step.titleSuffix}</span>
      </h3>
    );
  }
  return (
    <h3
      style={{
        fontFamily: 'Coolvetica, sans-serif',
        fontSize: 'clamp(24px, 6vw, 30px)',
        fontWeight: 400,
        letterSpacing: '-0.025em',
        lineHeight: 'clamp(28px, 6.4vw, 34px)',
        marginBottom: '16px',
        textAlign: 'center',
      }}
    >
      <span style={{ color: '#FF98BF' }}>{step.titleHighlight}</span>{' '}
      <span style={{ color: '#000' }}>{step.titleRest}</span>
    </h3>
  );
};

export default DemosEvolvedSection;
